import Big from 'big.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import {
  RegisterBanksSelect,
  mapTransactionTypeToBankType,
} from '@/modules/common/components/RegisterBanksSelect.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { useQuantityPricePerShareTotal } from '@/modules/common/hooks/useQuantityPricePerShareTotal.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useRegisterQuery } from '@/modules/common/queries/registers.ts';
import { useRegisterClassListInfinityQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import FractionalShares from '@/modules/issuer-dashboard/components/FractionalShares.tsx';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import { useTransactionAwaitingPaymentMutation } from '@/modules/issuer-dashboard/queries/transactions.ts';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  CalendarInputProps,
  ModalContentWrapper,
  SelectPaginate,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import {
  RegisterBankExtended,
  RoundTo,
  TransactionSetAwaitPaymentStatusRequest,
  TransactionStatus,
  TransactionType,
} from 'api';

dayjs.extend(utc);

const validationSchema = yup.object<
  TransactionSetAwaitPaymentStatusRequest & {
    total: number;
    issue_date: string;
  }
>({
  quantity: yup
    .number()
    .typeError('Quantity must be a number')
    .moreThan(0, 'Quantity must be more than 0')
    .required('This field is required'),
  total: yup.number().typeError('Total must be a number').required('This field is required'),
  register_class_id: yup
    .number()
    .typeError('Security class id must be a number')
    .required('This field is required')
    .moreThan(0, 'This field is required'),
  price_per_share: yup
    .number()
    .typeError('Price must be a number')
    .required('This field is required'),
  memo: yup.string().trim(),
  issue_date: yup.string().trim().required('This field is required'),
  bank_id: yup.number().moreThan(0, 'This field is required').required('This field is required'),
});

const initialValues: TransactionSetAwaitPaymentStatusRequest & {
  total: number;
  issue_date: string;
} = {
  id: 0,
  register_class_id: 0,
  quantity: 0,
  price_per_share: 0,
  total: 0,
  memo: '',
  issue_date: '',
  bank_id: 0,
  round_to: RoundTo.Up,
};

interface RegisterClassSelectPaginate {
  id: number;
}

export interface IssuerConfirmTransactionModalProps extends BaseModalProps {
  id: number;
  register_class_id: number;
  register_class_name: string;
  quantity: number;
  price_per_share: number;
  register_id: number;
  status?: TransactionStatus | string;
  type?: TransactionType | string;
  total?: number;
  issue_date?: string;
  // currentPricePerShare?: number;
}

export const IssuerConfirmTransactionModal = ({
  id,
  open,
  quantity,
  register_id,
  price_per_share,
  register_class_id,
  register_class_name,
  onClose,
  status,
  type,
  total,
  issue_date,
}: // currentPricePerShare,
IssuerConfirmTransactionModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const allowsFractionalShares = useGetRegister()?.allows_fractional_shares ?? true;
  const isPendingDrpTransaction: boolean =
    type === TransactionType.Drp &&
    (status === TransactionStatus.Pending || status === TransactionStatus.DividendPending);
  const formik = useFormik<
    TransactionSetAwaitPaymentStatusRequest & { total: number; issue_date: string }
  >({
    initialValues: {
      ...initialValues,
      id,
      register_class_id,
      quantity,
      total: quantity
        ? Number(
            new Big(quantity).mul(price_per_share).round(2, BigRoundMode.roundHalfUp).valueOf(),
          )
        : total
        ? Number(total)
        : 0,
      price_per_share: price_per_share,
      round_to: RoundTo.Up,
      issue_date:
        isPendingDrpTransaction && !!issue_date
          ? dayjs(issue_date).utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]')
          : dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]'),
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: isPendingDrpTransaction ? undefined : validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!values.bank_id || values.bank_id <= 0) {
        formik.setFieldError('bank_id', 'This field is required');
        return;
      }

      if (isPendingDrpTransaction) {
        const updatedValues = {
          ...values,
          issue_date: undefined,
        };
        await confirmTransaction(updatedValues as TransactionSetAwaitPaymentStatusRequest);
      } else {
        await confirmTransaction(values as TransactionSetAwaitPaymentStatusRequest);
      }
      resetForm();
      onClose();
    },
    // enableReinitialize: true,
  });

  const { data: registerData } = useRegisterQuery(register_id, !register_id);

  const { rounding_type, allows_fractional_shares = true } = useMemo(() => {
    const { rounding_type, allows_fractional_shares } = registerData?.data ?? {};
    return { rounding_type, allows_fractional_shares };
  }, [registerData]);

  const { onChangePricePerShare, onChangeQuantity, onChangeTotal } = useQuantityPricePerShareTotal({
    formik,
    allowsFractionalSharesQuantity: allows_fractional_shares,
    roundingTypeQuantity: rounding_type,
  });

  useEffect(() => {
    if (
      isPendingDrpTransaction &&
      !formik.values.quantity &&
      total &&
      price_per_share &&
      formik.values.price_per_share
    ) {
      let num = new Big(total).div(formik.values.price_per_share);

      if (allows_fractional_shares) {
        num = num.round(2, BigRoundMode.roundHalfUp);
      } else {
        num =
          formik.values.round_to === RoundTo.Down
            ? num.round(0, BigRoundMode.roundDown)
            : num.round(0, BigRoundMode.roundUp);
      }
      const nextQuantity = Number(num.valueOf());
      if (Number(formik.values.quantity) !== nextQuantity) {
        formik.setFieldValue('quantity', nextQuantity);
      }
    }
  }, [
    formik.values.price_per_share,
    formik.values.quantity,
    formik.values.round_to,
    isPendingDrpTransaction,
    price_per_share,
    allows_fractional_shares,
    total,
  ]);

  useEffect(() => {
    if (isPendingDrpTransaction && !!formik.values.price_per_share && !!formik.values.quantity) {
      const newTotal = Number(
        new Big(formik.values.quantity)
          .mul(formik.values.price_per_share)
          .round(2, BigRoundMode.roundDown)
          .valueOf(),
      );
      if (total && newTotal > total) {
        // TODO: Round down to the number which fits the initial total of this holder (dividend.amount per share * holder.shares)
      }
      if (Number(formik.values.total) !== newTotal) {
        formik.setFieldValue('total', newTotal);
      }
    }
  }, [
    formik,
    formik.values.quantity,
    formik.values.total,
    isPendingDrpTransaction,
    allows_fractional_shares,
    total,
  ]);

  const { mutateAsync: confirmTransaction } = useTransactionAwaitingPaymentMutation({
    setErrors: formik.setErrors,
  });
  const { fetchNextPage: fetchNextRegisterClassListPage } = useRegisterClassListInfinityQuery(
    {
      page: 1,
      page_size: 15,
      register_id,
    },
    true,
  );
  const handleAsyncRegisterClassList = async (currentPage: number, search?: string) => {
    const response = await fetchNextRegisterClassListPage({
      pageParam: {
        page: currentPage,
        search,
      },
    });

    const options: SelectPaginateOption<RegisterClassSelectPaginate>[] =
      response.data?.pages.reduce((acc: any, { data, page }) => {
        if (page !== currentPage) {
          return acc;
        }

        return data.map(({ class_name, id, current_price_per_share }) => ({
          label: class_name,
          value: { id, price_per_share: current_price_per_share, toString: () => class_name },
        }));
      }, []) || [];
    return options;
  };

  const handleChangeRegisterClass = (register_class_id: number) => {
    formik.setFieldValue('register_class_id', register_class_id);
    formik.setFieldError('register_class_id', '');
  };

  const handleChangeBank = (item: SelectPaginateOption<RegisterBankExtended> | null) => {
    formik.setFieldValue('bank_id', item?.value.id);
    formik.setFieldError('bank_id', '');
  };

  const handleChangeDate = useCallback(
    (fieldName: keyof IssuerConfirmTransactionModalProps): CalendarInputProps['onChange'] =>
      date => {
        if (!date) {
          formik.setFieldValue(fieldName, '');
          formik.setFieldError(fieldName, '');
          return;
        }
        const currentDate = date.utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]');
        formik.setFieldValue(fieldName, currentDate);
        formik.setFieldError(fieldName, '');
      },
    [],
  );

  const amountExcess = useMemo(
    () =>
      formik?.values?.total && formik.values.price_per_share
        ? Number(new Big(formik.values.total || 0).mod(formik.values.price_per_share).valueOf())
        : 0,
    [formik?.values?.total, formik.values.price_per_share],
  );

  const isAmountFractional = amountExcess !== 0;

  const roundDownShares = useMemo(
    () =>
      formik?.values?.total && formik.values.price_per_share
        ? Number(
            new Big(formik.values.total)
              .div(formik.values.price_per_share)
              .round(0, BigRoundMode.roundDown)
              .valueOf(),
          )
        : 0,
    [formik.values.total, formik.values.price_per_share],
  );

  const roundUpShares = useMemo(
    () =>
      formik?.values?.total && formik.values.price_per_share
        ? Number(
            new Big(formik.values.total)
              .div(formik.values.price_per_share)
              .round(0, BigRoundMode.roundUp)
              .valueOf(),
          )
        : 0,
    [formik.values.total, formik.values.price_per_share],
  );

  return (
    <ModalContentWrapper open={open} title={'Confirm transaction'} onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col gap-8" onSubmit={formik.handleSubmit}>
          <div className="w-full flex flex-col gap-8">
            <RegisterBanksSelect
              onChange={handleChangeBank}
              value={formik.values.bank_id}
              label="Bank"
              clearable={true}
              error={formik.errors.bank_id}
              needBankType={mapTransactionTypeToBankType[type as TransactionType]}
              needClassId={formik.values.register_class_id}
              is_overall={true}
            />
            <CalendarInput
              name="issue_date"
              onChange={handleChangeDate('issue_date')}
              error={formik.errors.issue_date}
              label="Units issue date"
              value={formik.values.issue_date ? dayjs(formik.values.issue_date) : undefined}
              disabled={isPendingDrpTransaction}
              format="DD-MM-YYYY HH:mm"
              showTime={true}
            />
            <CurrencyInput
              currency=""
              name="quantity"
              label="quantity of shares"
              value={formik.values.quantity}
              allowFractional={allows_fractional_shares}
              roundingType={rounding_type}
              onChange={onChangeQuantity}
              disabled={isPendingDrpTransaction}
            />
            {isPendingDrpTransaction && (
              <CurrencyInput
                name="price_per_share"
                label={`price per share/unit (${getRegisterTypeStrings('nav', {
                  registerId: register_id,
                })})`}
                value={formik.values.price_per_share}
                decimalPoints={4}
                error={formik.errors.price_per_share}
                onChange={onChangePricePerShare}
              />
            )}
            <CurrencyInput
              name="total"
              label="total"
              error={formik.errors.total}
              value={formik.values.total}
              onChange={onChangeTotal}
              disabled={isPendingDrpTransaction}
            />
            <SelectPaginate<RegisterClassSelectPaginate>
              label="class"
              onAsync={handleAsyncRegisterClassList}
              onChange={option => handleChangeRegisterClass(option.value.id)}
              defaultValue={{
                label: register_class_name,
                value: { id: register_class_id },
              }}
              disabled={isPendingDrpTransaction}
            />
            {!isPendingDrpTransaction && (
              <>
                <CurrencyInput
                  name="price_per_share"
                  label={`price per share/unit (${getRegisterTypeStrings('nav', {
                    registerId: register_id,
                  })})`}
                  decimalPoints={4}
                  value={formik.values.price_per_share}
                  error={formik.errors.price_per_share}
                  onChange={onChangePricePerShare}
                />
                <TextInputField
                  textarea
                  name="memo"
                  label="memo (optional)"
                  placeholder="Memo..."
                  className="resize-none"
                />
              </>
            )}
            {isPendingDrpTransaction && !allowsFractionalShares && isAmountFractional && (
              <FractionalShares
                amount={formik.values.total}
                price_per_share={formik.values.price_per_share ?? price_per_share}
                amountExcess={amountExcess}
                value={formik.values.round_to ?? RoundTo.Up}
                roundDownShares={roundDownShares}
                roundUpShares={roundUpShares}
                onChange={value => formik.setFieldValue('round_to', value)}
              />
            )}
          </div>
          <Button type="submit" loading={formik.isSubmitting} disabled={formik.isSubmitting}>
            Confirm
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
